import { performanceStats, engagementTimeline, clients } from "../../../mocks/platformData";

const learningFeatures = [
  { icon: "ri-database-2-line", title: "과거 성과 데이터 저장", desc: "게시된 모든 클립의 지표가 저장되고 색인화되어 지속적인 모델 개선에 활용됩니다." },
  { icon: "ri-loop-left-line", title: "자동 모델 재학습", desc: "감지 모델이 누적된 성과 데이터로 매주 재학습되어 지속적으로 정밀도가 향상됩니다." },
  { icon: "ri-trending-up-line", title: "바이럴 가능성 예측", desc: "과거 패턴과 실시간 신호를 기반으로 게시 전 클립의 바이럴 잠재력을 예측합니다." },
  { icon: "ri-robot-2-line", title: "자기 개선 파이프라인", desc: "시스템은 시간이 지날수록 더 정확해집니다. 많이 방송할수록 AI가 더 똑똑해집니다." },
];

const maxViewers = Math.max(...engagementTimeline.map((d) => d.viewers));
const maxEngagement = Math.max(...engagementTimeline.map((d) => d.engagement));

export default function PerformanceSection() {
  return (
    <section id="analytics" className="py-28 bg-[#0F1419] relative overflow-hidden">
      <div className="absolute top-0 right-1/4 w-80 h-80 rounded-full bg-[#00D9FF]/5 blur-3xl pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span className="text-[#00D9FF] text-xs font-semibold uppercase tracking-widest mb-4 block" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            08 / 성과 분석 및 인사이트
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            스스로 <span className="text-[#00D9FF]">발전하는</span> 데이터
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
            방송 에피소드에서 생성된 모든 클립의 성과가 피드백 루프에 반영됩니다.
            어떤 장면이 바이럴되었는지 학습하며 감지 정밀도를 지속적으로 높입니다.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 mb-12">
          {performanceStats.map((stat) => (
            <div key={stat.label} className="bg-[#0A0E14] border border-white/8 rounded-2xl p-6 text-center">
              <div className="text-4xl font-bold text-white mb-2" style={{ fontFamily: "'JetBrains Mono', monospace", color: "#00D9FF" }}>
                {stat.value}
              </div>
              <div className="text-gray-500 text-xs mb-3" style={{ fontFamily: "'Inter', sans-serif" }}>{stat.label}</div>
              <div className={`text-xs font-bold px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${stat.up ? "text-[#10B981] bg-[#10B981]/10" : "text-[#EF4444] bg-[#EF4444]/10"}`} style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                <span className="w-3 h-3 flex items-center justify-center">
                  <i className={stat.up ? "ri-arrow-up-line text-xs" : "ri-arrow-down-line text-xs"} />
                </span>
                {stat.change}
              </div>
            </div>
          ))}
        </div>

        <div className="bg-[#0A0E14] border border-white/8 rounded-2xl p-8 mb-12">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-white font-semibold text-base mb-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>콘텐츠 감정 강도 vs. 바이럴 성과 타임라인</h3>
              <p className="text-gray-500 text-sm">방송 에피소드 시간대별 AI 감지 하이라이트 오버레이</p>
            </div>
            <div className="flex items-center gap-4 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              <span className="flex items-center gap-1.5 text-[#00D9FF]"><span className="w-3 h-0.5 bg-[#00D9FF] inline-block rounded" />시청자 수</span>
              <span className="flex items-center gap-1.5 text-[#10B981]"><span className="w-3 h-0.5 bg-[#10B981] inline-block rounded" />참여도</span>
              <span className="flex items-center gap-1.5 text-[#F59E0B]"><span className="w-3 h-3 rounded-full bg-[#F59E0B]/30 border border-[#F59E0B] inline-block" />하이라이트</span>
            </div>
          </div>
          <div className="relative" style={{ height: "200px" }}>
            <svg className="w-full h-full" viewBox="0 0 900 200" preserveAspectRatio="none">
              {[0, 50, 100, 150, 200].map((y) => (
                <line key={y} x1="0" y1={y} x2="900" y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />
              ))}
              <defs>
                <linearGradient id="viewerGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#00D9FF" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#00D9FF" stopOpacity="0" />
                </linearGradient>
                <linearGradient id="engagementGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10B981" stopOpacity="0.2" />
                  <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={`M ${engagementTimeline.map((d, i) => `${(i / (engagementTimeline.length - 1)) * 900},${200 - (d.viewers / maxViewers) * 180}`).join(" L ")} L 900,200 L 0,200 Z`} fill="url(#viewerGrad)" />
              <polyline points={engagementTimeline.map((d, i) => `${(i / (engagementTimeline.length - 1)) * 900},${200 - (d.viewers / maxViewers) * 180}`).join(" ")} fill="none" stroke="#00D9FF" strokeWidth="2.5" />
              <path d={`M ${engagementTimeline.map((d, i) => `${(i / (engagementTimeline.length - 1)) * 900},${200 - (d.engagement / maxEngagement) * 180}`).join(" L ")} L 900,200 L 0,200 Z`} fill="url(#engagementGrad)" />
              <polyline points={engagementTimeline.map((d, i) => `${(i / (engagementTimeline.length - 1)) * 900},${200 - (d.engagement / maxEngagement) * 180}`).join(" ")} fill="none" stroke="#10B981" strokeWidth="2" strokeDasharray="6 3" />
              {engagementTimeline.filter((d) => d.highlights > 0).map((d, i) => (
                <circle key={i} cx={`${(engagementTimeline.indexOf(d) / (engagementTimeline.length - 1)) * 900}`} cy={`${200 - (d.engagement / maxEngagement) * 180}`} r="6" fill="rgba(245,158,11,0.3)" stroke="#F59E0B" strokeWidth="2" />
              ))}
            </svg>
            <div className="absolute bottom-0 left-0 right-0 flex justify-between px-0 pt-2 border-t border-white/8">
              {engagementTimeline.map((d) => (
                <span key={d.time} className="text-gray-600 text-xs" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{d.time}</span>
              ))}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>지속 학습 및 피드백 루프</h3>
            <div className="space-y-4">
              {learningFeatures.map((f) => (
                <div key={f.title} className="flex items-start gap-3 bg-[#0A0E14] border border-white/8 rounded-xl p-4">
                  <div className="w-9 h-9 flex items-center justify-center bg-[#00D9FF]/10 rounded-lg shrink-0">
                    <i className={`${f.icon} text-[#00D9FF] text-base`} />
                  </div>
                  <div>
                    <div className="text-white text-sm font-semibold mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{f.title}</div>
                    <p className="text-gray-500 text-xs leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-white font-bold text-xl mb-6" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>고객 성과</h3>
            <div className="space-y-4">
              {clients.map((c) => (
                <div key={c.name} className="bg-[#0A0E14] border border-white/8 rounded-xl p-5 flex items-center justify-between gap-4">
                  <div>
                    <div className="text-white font-semibold mb-0.5" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>{c.name}</div>
                    <div className="text-gray-500 text-xs">{c.category}</div>
                  </div>
                  <div className="flex gap-6 text-right">
                    <div>
                      <div className="text-[#00D9FF] font-bold text-base" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.clips}</div>
                      <div className="text-gray-600 text-xs">클립</div>
                    </div>
                    <div>
                      <div className="text-[#10B981] font-bold text-base" style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.views}</div>
                      <div className="text-gray-600 text-xs">조회 수</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
